<template>
  <div>
    <!-- Toolbar -->
    <div class="toolbar d-flex flex-wrap justify-content-end mb-2">
      <div class="d-flex flex-wrap justify-content-center justify-content-md-start">
        <button variant="success"
                data-toggle="modal"
                data-target="#userModal"
                class="btn btn-success btn-sm"
                @click="handleAddForm()">
          <i class="fal fa-plus-circle mr-1"></i>
          Add new User
        </button>
      </div>
    </div>

    <div v-if="paginatedUsers && !loading">
      <div v-if="paginatedUsers.length"
           class="table-responsive">
        <table class="table _datatable users-table --very-small-height">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email </th>
              <th>Date Joined</th>
              <th></th>
            </tr>
          </thead>
          <tbody :class="{ '--loading': miniLoading }">
            <tr v-for="(row, index) in paginatedUsers"
                :key="index">

              <td class="--width-md">
                {{ row.username }}
              </td>

              <td class="--width-md">
                {{ row.email }}
              </td>

              <td v-html="tdHtml('createdAtDate', row.createdAt)"></td>

              <td>
                <Button variant="link"
                        size="sm"
                        custom_class="mr-2 p-0 text-color-5"
                        id="btn-action-edit"
                        @click="editItem(row)">
                  <i class="fal fa-edit"></i>
                </Button>

                <Button variant="link"
                        custom_class="p-0 text-color-5 mr-2"
                        size="sm"
                        id="btn-action-delete"
                        @click="deleteItem(row)">
                  <i class="fal fa-times-circle"></i>
                </Button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="d-flex justify-content-between">
          <div>
            <select class="form-control"
                    v-model="perPage"
                    @change="handlePerPageChange()">
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="300">300</option>
              <option value="500">500</option>
            </select>
          </div>

          <div class="d-flex">
            <p class="align-self-center mr-2 text-color-4">
              หน้าที่ {{ page }}
            </p>

            <div class="btn-group"
                 role="group">
              <Button type="button"
                      variant="outline-primary"
                      size="lg"
                      id="btn-paginate-prev"
                      @click="paginate(false)"
                      :disabled="page === 1">
                <i class="fal fa-long-arrow-left"></i>
              </Button>

              <Button type="button"
                      variant="outline-primary"
                      size="lg"
                      id="btn-paginate-prev"
                      @click="paginate(true)"
                      :disabled="page === lastPage"
                      :loading="miniLoading">
                <i class="fal fa-long-arrow-right"></i>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!paginatedUsers.length"
           class="alert alert-warning mt-5 mb-5">
        No Users found
      </div>
    </div>

    <div v-else>
      <Spinner :variant="'primary'"
               size="md" />
    </div>

    <Modal id="userModal">
      <template slot="header">
        <h5 class="modal-title">
          <i class="mr-2 fal fa-users"></i>
          <span v-if="formMode === 0">สร้างลูกค้าใหม่</span>
          <span v-else-if="formMode === 1">สร้างลูกค้าใหม่</span>
        </h5>
      </template>

      <template slot="body">
        <ValidationObserver v-slot="{ invalid }">
          <form ref="userForm"
                @submit.prevent="handleFormSubmit">

            <div class="row">
              <div class="col-3">
                <p class="text-nowrap">Username</p>
              </div>
              <div class="col-9">
                <TextBox type="text"
                         size="sm"
                         id="username"
                         placeholder="Username"
                         rules="required"
                         v-model="userForm.username" />
              </div>
            </div>

            <div class="row">
              <div class="col-3">
                <p class="text-nowrap">Email</p>
              </div>
              <div class="col-9">
                <TextBox type="email"
                         size="sm"
                         id="email"
                         placeholder="Email"
                         rules="required"
                         v-model="userForm.email" />
              </div>
            </div>

            <div class="row">
              <div class="col-3">
                <p class="text-nowrap">Password</p>
              </div>
              <div class="col-9">
                <TextBox type="password"
                         size="sm"
                         id="password"
                         placeholder="Password"
                         :rules="formMode === 0 ? 'required' : null"
                         v-model="userForm.password" />
              </div>
            </div>

            <div class="row">
              <div class="col-3">
                <p class="text-nowrap">Password Confirm</p>
              </div>
              <div class="col-9">
                <TextBox type="password"
                         size="sm"
                         id="passwordConfirmation"
                         placeholder="Password Confirm"
                         :rules="formMode === 0 ? 'required' : null"
                         v-model="userForm.passwordConfirmation" />
              </div>
            </div>

            <div class="d-flex justify-content-center">
              <Button custom_class="pl-4 pr-4 btn-block"
                      type="submit"
                      id="btn-submit"
                      size="md"
                      :disabled="invalid || formLoading"
                      variant="primary"
                      :loading="formLoading">
                <span v-if="formMode === 0">สร้างลูกค้า</span>
                <span v-if="formMode === 1">ยืนยันการแก้ไข</span>
              </Button>
            </div>
          </form>
        </ValidationObserver>
      </template>
    </Modal>

  </div>
</template>

<script>
import TextBox from "@/components/core/TextBox";
import Modal from "@/components/core/Modal";
import Button from "@/components/core/Button";
import { Alert } from "@/helpers/alert";
import Spinner from "@/components/core/Spinner";
import { FILTERS } from "@/helpers/filters";
import _ from "lodash";

export default {
  name: "UsersTable",
  components: {
    TextBox,
    Modal,
    Button,
    Spinner
  },
  data() {
    return {
      userRows: null,
      userCounts: null,
      LastEvaluatedKey: null,
      paginatedUsers: [],

      loading: false,
      miniLoading: false,
      modalLoading: false,
      formLoading: false,

      formMode: 0,
      userForm: null,

      page: 1,
      perPage: 100,
      lastPage: null
    };
  },

  created() {
    this.initForm();
  },

  async mounted() {
    await this.fetchUsers()
  },

  methods: {
    tdHtml(type, value) {
      const types = {
        createdAtDate: (value) => {
          return FILTERS.formatDate(value);
        }
      };
      return types[type] ? types[type](value) : value;
    },

    paginate(forward = true) {
      if (forward) {
        if (!(this.lastPage && this.page === this.lastPage))
          this.page = this.page + 1;
      } else {
        this.page = Math.max(--this.page, 1);
      }
      this.handlePageChange();
    },

    async fetchUsers(firstPage = true, _callback) {
      if (firstPage) this.loading = true;
      else this.miniLoading = true;

      const { status, data } = await this.$store.dispatch("user/fetchUsers", {
        params: {
          ExclusiveStartKey: !firstPage ? JSON.stringify(this.LastEvaluatedKey) : null,
          perPage: this.perPage
        }
      });

      if (status && data) {
        this.LastEvaluatedKey = data.LastEvaluatedKey;

        if (firstPage) {
          this.userRows = data.users;
          this.page = 1;
          this.lastPage = null;
        } else {
          this.userRows = this.userRows.concat(data.users);
        }

        // LOGIC: check from counter
        this.lastPage = data.LastEvaluatedKey ? false : true;

        if (this.userRows.length) {
          this.handlePageChange();
        } else {
          this.loading = false;
          this.miniLoading = false;
        }
      }

      if (firstPage) this.loading = false;
      else this.miniLoading = false;

      if (_callback) _callback();
    },

    handlePageChange() {
      let startIndex = (this.page - 1) * this.perPage;
      let endIndex = this.perPage * this.page - 1;

      // check if it needs data to load from server
      if (
        this.userRows.length <= endIndex &&
        this.LastEvaluatedKey &&
        this.page != this.lastPage
      ) {
        this.fetchUsers(this.currentTabStatus, false, () => {
          this.paginatedUsers = this.userRows.slice(startIndex, endIndex + 1);
        });
      } else {
        this.paginatedUsers = this.userRows.slice(startIndex, endIndex + 1);
      }

      //handle empty page
      if (this.paginatedUsers.length === 0) {
        this.page--;
        this.lastPage = this.page;
        this.handlePageChange();
        this.LastEvaluatedKey = null;
      }
    },

    handlePerPageChange() {
      this.page = 1;
      this.handlePageChange();
    },

    handleAddForm() {
      this.formMode = 0;
      this.initForm();
    },

    initForm() {
      this.userForm = {
        username: null,
        email: null,
        password: null,
        passwordConfirmation: null
      };
    },

    deleteItem(user) {
      Alert(
        "Delete User",
        "Are you sure you want to delete this user?",
        null,
        async () => {
          this.miniLoading = true;
          const { status } = await this.$store.dispatch("user/deleteUser", { id: `${user.id}__${user.createdAt}` });
          if (status) {
            let index = this.paginatedUsers.findIndex(s => s.id === user.id)
            if (index !== -1) {
              this.paginatedUsers.splice(index, 1)
            }
          }
          this.miniLoading = false;
        }
      );
    },

    editItem(user) {
      this.userForm = JSON.parse(JSON.stringify(user));
      this.formMode = 1;
      $("#userModal").modal("show");
    },

    async handleFormSubmit() {
      this.formLoading = true;
      if (this.formMode === 0) {
        const { status, data } = await this.$store.dispatch("user/createUser", this.userForm);
        if (status) {
          this.paginatedUsers.unshift(data)
          $("#userModal").modal("hide");
        }
      } else {
        const { status } = await this.$store.dispatch("user/updateUser", {
          id: `${this.userForm.id}__${this.userForm.createdAt}`,
          data: {
            username: this.userForm.username,
            email: this.userForm.email,
            password: this.userForm.password,
            passwordConfirmation: this.userForm.passwordConfirmation,
          }
        })
        if (status) {
          let index = this.paginatedUsers.findIndex(s => s.id === this.userForm.id)
          if (index !== -1) {
            const _users = JSON.parse(JSON.stringify(this.paginatedUsers))
            _users[index] = JSON.parse(JSON.stringify(this.userForm))
            this.paginatedUsers = _users
          }
          $("#userModal").modal("hide");
        }
      }

      this.formLoading = false;
    }
  }
};
</script>